import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SplashPage } from './pages/splash';
import { SignInPage } from './pages/sign-in';
import { CreateAccountPage } from './pages/create-account';
import { ProfilePage } from './pages/profile';
import { UsersPage } from './pages/users';
import { UserDetailsPage } from './pages/user-details';
import { EventsPage } from './pages/events';
import { EventDetailsPage } from './pages/event-details';
import { EventsAdminPage } from './pages/events-admin';
import { EventDetailsAdminPage } from './pages/event-details-admin';
import PartnerDiscoveryPage from './pages/partner-discovery';
import { Header } from './components/Header';
import { PasswordGate } from './components/PasswordGate';
import { MatchNotificationProvider } from './context/MatchNotificationContext';
import { initializeAuth, getCurrentUser } from './lib/api-client';
import './index.css';

// Create custom Material UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily:
      '"Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "sans-serif"',
    h1: {
      fontWeight: 800,
    },
    h4: {
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const WithHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <Header />
      {children}
    </>
  );
};

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize auth state from server on app startup
    initializeAuth().finally(() => {
      setIsInitialized(true);
    });
  }, []);

  // Show loading spinner while checking auth state
  if (!isInitialized) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MatchNotificationProvider>
        <Router>
          <Routes>
            {/* Root redirects to Events */}
            <Route path="/" element={<Navigate to="/events" replace />} />

            {/* Sign In Page */}
            <Route path="/sign-in" element={<SignInPage />} />

            {/* Create Account Page */}
            <Route path="/create-account" element={<CreateAccountPage />} />

            {/* Profile Manager Page */}
            <Route
              path="/profile"
              element={
                <WithHeader>
                  <ProfilePage />
                </WithHeader>
              }
            />

            {/* User Details Page */}
            <Route
              path="/users/:userid"
              element={
                <WithHeader>
                  <UserDetailsPage />
                </WithHeader>
              }
            />

            {/* Users Page */}
            <Route
              path="/users"
              element={
                <WithHeader>
                  <UsersPage />
                </WithHeader>
              }
            />

            {/* Events Admin Pages */}
            <Route
              path="/events-admin/:eventId"
              element={
                <WithHeader>
                  <EventDetailsAdminPage />
                </WithHeader>
              }
            />
            <Route
              path="/events-admin"
              element={
                <WithHeader>
                  <EventsAdminPage />
                </WithHeader>
              }
            />

            {/* Event Details Page */}
            <Route
              path="/events/:eventId"
              element={
                <WithHeader>
                  <EventDetailsPage />
                </WithHeader>
              }
            />
            {/* Events Page */}
            <Route
              path="/events"
              element={
                <WithHeader>
                  <EventsPage />
                </WithHeader>
              }
            />

            {/* Partner Discovery Page */}
            <Route
              path="/discover"
              element={
                <WithHeader>
                  <PartnerDiscoveryPage />
                </WithHeader>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </MatchNotificationProvider>
    </ThemeProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PasswordGate>
      <App />
    </PasswordGate>
  </React.StrictMode>
);
